import type { ProfileWorkflowInstance } from '../models/vendor-registration-profile.model';

/**
 * Nama langkah workflow yang berarti "formulir masih boleh disunting vendor".
 *
 * Literal string yang dibandingkan dengan `name` dari BE, **bukan** `ident_name`: itulah yang
 * dipakai aplikasi acuan (`isCurrentWorkflowStepName(…, 'Form Submission')`), dan `ident_name`
 * belum tentu terisi di seluruh instalasi. Konsekuensinya perbandingan ini peka terhadap
 * perubahan nama langkah di master workflow BE — kalau langkahnya diganti nama, tombol "Tambah
 * Data Baru" akan mati tanpa satu pun error. Ditulis di sini sebagai satu konstanta supaya
 * perbaikannya satu baris.
 */
export const FORM_SUBMISSION_STEP_NAME = 'Form Submission';

/**
 * `system_desc` log yang ditulis BE saat reviewer MENGEMBALIKAN pengajuan (aksi Tolak → `restart`).
 *
 * Literal string dari BE, sama rapuhnya dengan `FORM_SUBMISSION_STEP_NAME` di atas: kalau BE
 * mengganti kalimatnya, banner "Perlu Revisi" berhenti muncul tanpa satu pun error.
 */
export const WORKFLOW_RESTARTED_DESC = 'Workflow restarted';

/**
 * Langkah yang SEDANG berjalan — satu-satunya `child` ber-`current === true`.
 *
 * `=== true`, bukan truthy: BE mengirim `null` (bukan `false`) untuk langkah yang tidak aktif, dan
 * keduanya memang harus jatuh ke cabang yang sama. Dicari HANYA di antara `children` langsung,
 * tidak rekursif — persis `getCurrentWorkflowStep` acuan.
 */
export function currentWorkflowStep(
  workflow: ProfileWorkflowInstance | null | undefined,
): ProfileWorkflowInstance | null {
  if (isWorkflowEmpty(workflow)) return null;
  return workflow?.children.find((child) => child.current === true) ?? null;
}

/**
 * Alur kerja BELUM DIMULAI.
 *
 * ⚠️ Gerbangnya `children.length === 0`, dan itu satu-satunya yang benar-benar kena di praktik:
 * serializer BE selalu mengeluarkan objek `workflow_instance` (berkunci lengkap bernilai `null`)
 * walau alurnya belum ada — jadi memeriksa `workflow == null` saja akan SELALU menyimpulkan
 * "sudah dimulai". Cabang `!workflow` dipertahankan sebagai pertahanan, mengikuti `workflowEmpty`
 * acuan.
 */
export function isWorkflowEmpty(workflow: ProfileWorkflowInstance | null | undefined): boolean {
  return !workflow || workflow.children.length === 0;
}

/** Langkah yang sedang berjalan bernama `name`. Terjemahan `isCurrentWorkflowStepName` acuan. */
export function isCurrentWorkflowStepName(
  workflow: ProfileWorkflowInstance | null | undefined,
  name: string,
): boolean {
  return currentWorkflowStep(workflow)?.name === name;
}
